<template>
  <div class="batch-samples-batch-confirmation">
    <batch-move-results-table
      v-if="showResultsView"
      :results="moveResults"
      :summary="moveSummary"
      :is-retrying="isMoving"
      :batch="batch"
      :target-batch-identifier="batch.batchIdentifier"
      @retry-failed="handleRetryFailed"
      @close="handleResultsClose"
      @open-batch="handleOpenBatch"
    />

    <batch-move-overload-view
      v-else-if="showOverloadView"
      :retry-after="moveRetryAfter"
      @retry="handleContinue"
      @close="handleOverloadClose"
    />

    <template v-else>
      <div class="batch-samples-batch-confirmation-content">
        <div class="batch-samples-batch-confirmation-destination">
          <span class="batch-samples-batch-confirmation-destination-label">Lote destino:</span>
          <span class="mnemonic-badge">{{ mnemonicLabel }}</span>
          <span
            v-if="batch.urgent"
            class="urgent-badge"
          >
            <svg-loader src="/static/images/icon-urgent-fast.svg" />
          </span>
          <span class="batch-samples-batch-confirmation-destination-identifier">{{ batch.batchIdentifier }}</span>
        </div>
      </div>

      <footer class="batch-samples-batch-confirmation-footer">
        <md-button
          class="btn btn-nonenary"
          @click="handleCancel"
        >
          Cancelar
        </md-button>

        <md-button
          class="btn btn-primary"
          :disabled="isMoving"
          @click="handleContinue"
        >
          Continuar
        </md-button>
      </footer>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import notify from '@/api/notification'
import { MAX_SAMPLES_PER_BATCH } from '@/constants/batch'
import { translateBatchErrorFromResponse, resolveConflictToastTitle } from '@/utils/batchErrorCodes'

import { useMoveSamplesBatch } from '@/composables/useMoveSamplesBatch'

import BatchMoveResultsTable from './BatchMoveResultsTable.vue'
import BatchMoveOverloadView from './BatchMoveOverloadView.vue'

export default {
  name: 'BatchSamplesBatchConfirmation',
  components: {
    BatchMoveResultsTable,
    BatchMoveOverloadView,
  },
  props: {
    sample: {
      type: Object,
      required: true,
    },
    batch: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const {
      state,
      summary,
      results,
      retryAfter,
      lastError,
      moveSamplesBatch,
      retryFailed,
      reset,
    } = useMoveSamplesBatch()

    return {
      moveState: state,
      moveSummary: summary,
      moveResults: results,
      moveRetryAfter: retryAfter,
      moveLastError: lastError,
      moveSamplesBatch,
      retryFailed,
      resetMoveState: reset,
    }
  },
  data() {
    return {
      isMoving: false,
      showResultsView: false,
      showOverloadView: false,
    }
  },
  computed: {
    ...mapGetters(['selectedSamples']),
    samplesToInsert() {
      return this.sample ? [this.sample] : this.selectedSamples
    },
    amount() {
      return this.samplesToInsert.length
    },
    mnemonicLabel() {
      if (!this.batch.label) return ''
      if (this.batch.sequence == null) return this.batch.label
      return `${this.batch.label}.${this.batch.sequence}`
    },
    modalTitle() {
      if (this.showResultsView) return 'Resultado do loteamento'
      return `Deseja inserir todas (${this.amount}) amostras no lote?`
    },
  },
  watch: {
    modalTitle: {
      immediate: true,
      handler(value) {
        this.$emit('update:title', value)
      },
    },
  },
  beforeDestroy() {
    this.resetMoveState()
  },
  methods: {
    handleCancel() {
      this.$emit('cancel')
    },
    translateBatchSampleValdation({ data }) {
      return data.type === 'validation' ? data?.description : data?.message
    },
    buildItems() {
      return this.samplesToInsert.map(sample => {
        const item = {
          barcode: sample.bar_code,
          targetBatchIdentifier: this.batch.batchIdentifier,
        }

        if (sample.batch_identifier) {
          item.sourceBatchIdentifier = sample.batch_identifier
        }

        return item
      })
    },
    async handleContinue() {
      const items = this.buildItems()

      if (items.length > MAX_SAMPLES_PER_BATCH) {
        notify({
          title: 'Limite excedido',
          message: `Selecione no máximo ${MAX_SAMPLES_PER_BATCH} amostras por lote.`,
        })
        return
      }

      this.isMoving = true
      this.showOverloadView = false

      const outcome = await this.moveSamplesBatch(items)

      this.isMoving = false

      this.applyOutcome(outcome)
    },
    async handleRetryFailed() {
      this.isMoving = true

      const outcome = await this.retryFailed()

      this.isMoving = false

      this.applyOutcome(outcome)
    },
    applyOutcome(outcome) {
      switch (outcome) {
        case 'SUCCESS_TOTAL':
          this.$emit('samplesBatched')
          this.$emit('openBatch', this.batch.batchIdentifier)
          this.$emit('close')
          break
        case 'PARTIAL':
          this.$emit('samplesBatched')
          this.showResultsView = true
          this.showOverloadView = false
          break
        case 'CONFLICT': {
          const conflictMessage =
            translateBatchErrorFromResponse(this.moveLastError) ||
            'Uma ou mais amostras já estão em outro lote. Atualize a listagem e tente novamente.'
          notify({
            title: resolveConflictToastTitle(this.moveLastError),
            message: conflictMessage,
          })
          this.$emit('samplesBatched')
          this.$emit('close')
          break
        }
        case 'OVERLOAD':
          this.showOverloadView = true
          this.showResultsView = false
          break
        case 'ERROR':
        default: {
          const errorResponse = this.moveLastError
          const structured = translateBatchErrorFromResponse(errorResponse)
          const legacyValidation = errorResponse
            ? this.translateBatchSampleValdation(errorResponse)
            : null
          notify({
            title: 'Erro:',
            message:
              structured ||
              legacyValidation ||
              'Ocorreu um problema inesperado ao tentar adicionar ao lote.',
          })
          break
        }
      }
    },
    handleResultsClose() {
      this.$emit('close')
    },
    handleOpenBatch(batchIdentifier) {
      this.$emit('openBatch', batchIdentifier || this.batch.batchIdentifier)
      this.$emit('close')
    },
    handleOverloadClose() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
.batch-samples-batch-confirmation {
  font-family: 'Exo 2', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-width: 32rem;

  &-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &-destination {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: #818181;

    &-label {
      font-weight: 700;
      text-transform: uppercase;
    }

    &-identifier {
      font-weight: 500;
      margin-left: 0.25rem;
    }
  }

  &-footer {
    display: flex;
    gap: 1rem;

    .md-button {
      flex: 1;
    }
  }

  .mnemonic-badge {
    background-color: #4a4a4a;
    border-radius: 2px;
    color: white;
    padding: 1px 6px;
    font-family: 'Exo 2';
    font-weight: 600;
    font-size: 12px;
    opacity: 0.93;
    text-transform: uppercase;
  }

  .urgent-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 4px;
    height: 20px;
    color: white;
    background: #ffbf00;
    border-radius: 4px 12px 12px 4px;

    svg {
      height: 16px;
      width: 16px;
    }
  }
}
</style>
